// Marketing-site → app deep-link landing page.
//
// The marketing site at clipbait.ai links here with query params:
//   /checkout?plan=pro&period=annual&credits=600&via=<rewardful_id>
//
// If the user is signed in we hit /billing/plan/checkout and bounce them
// straight to Stripe. If not, we send them to /login with a returnUrl so
// the params survive the auth round-trip.
import { useEffect, useState, useContext } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { axiosInstance } from "../../config";
import { getRewardfulReferral } from "../../utils/rewardful";

const VALID_PLANS = new Set(["starter", "pro", "premium"]);
const VALID_PERIODS = new Set(["monthly", "annual"]);

const SUCCESS_URL = "https://app.clipbait.ai/dashboard?welcome=1";
const CANCEL_URL = "https://clipbait.ai/#pricing";

export default function CheckoutHandoff() {
  const { user } = useContext(AuthContext);
  const { search } = useLocation();
  const [error, setError] = useState("");

  const params = new URLSearchParams(search);
  const plan = (params.get("plan") || "").toLowerCase();
  const period = (params.get("period") || "monthly").toLowerCase();
  const creditsRaw = params.get("credits");
  const credits = creditsRaw ? Number(creditsRaw) : undefined;
  const via = params.get("via") || (typeof window !== "undefined" ? localStorage.getItem("referral_via") : null);

  const planValid = VALID_PLANS.has(plan);
  const periodValid = VALID_PERIODS.has(period);

  useEffect(() => {
    if (!user || !planValid || !periodValid) return;

    let cancelled = false;
    (async () => {
      try {
        // Prefer the Rewardful referral UUID captured on THIS domain; fall
        // back to the ?via= the marketing site forwarded. Backend uses
        // `referral || via` as Stripe's client_reference_id.
        const referral = getRewardfulReferral();
        const res = await axiosInstance.post("/billing/plan/checkout", {
          plan,
          period,
          credits,
          referral,
          via,
          success_url: SUCCESS_URL,
          cancel_url: CANCEL_URL,
        });
        if (cancelled) return;
        const url = res.data?.url;
        if (!url) throw new Error("Stripe did not return a checkout URL");
        window.location.href = url;
      } catch (err) {
        if (cancelled) return;
        console.error("Checkout handoff failed:", err);
        setError(err.response?.data?.error || err.message || "Could not start checkout. Please try again.");
      }
    })();

    return () => { cancelled = true; };
  }, [user, plan, period, credits, via, planValid, periodValid]);

  if (!planValid || !periodValid) {
    return <Navigate to="/pricing" replace />;
  }

  if (!user) {
    const returnUrl = `/checkout${search}`;
    return <Navigate to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`} replace />;
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        {error ? (
          <>
            <h2 style={titleStyle}>Checkout error</h2>
            <p style={textStyle}>{error}</p>
            <a href="/pricing" style={linkStyle}>Back to pricing</a>
          </>
        ) : (
          <>
            <div style={spinnerStyle} />
            <h2 style={titleStyle}>Taking you to checkout…</h2>
            <p style={textStyle}>One sec while we set up your Clipbait plan.</p>
          </>
        )}
      </div>
    </div>
  );
}

const pageStyle = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#0a0a0a",
  color: "#fff",
  padding: "24px",
};

const cardStyle = {
  textAlign: "center",
  maxWidth: 420,
};

const titleStyle = {
  fontSize: 22,
  fontWeight: 600,
  margin: "16px 0 8px",
};

const textStyle = {
  fontSize: 14,
  color: "#aaa",
  margin: 0,
};

const linkStyle = {
  display: "inline-block",
  marginTop: 16,
  color: "#ff6a00",
  textDecoration: "none",
  fontWeight: 500,
};

const spinnerStyle = {
  width: 36,
  height: 36,
  margin: "0 auto",
  border: "3px solid #333",
  borderTopColor: "#ff6a00",
  borderRadius: "50%",
  animation: "cb-spin 0.8s linear infinite",
};

// Inject the keyframe once.
if (typeof document !== "undefined" && !document.getElementById("cb-spin-kf")) {
  const style = document.createElement("style");
  style.id = "cb-spin-kf";
  style.textContent = "@keyframes cb-spin { to { transform: rotate(360deg); } }";
  document.head.appendChild(style);
}
